import React, { useState, useRef, useEffect } from 'react';
import { 
    Box, Button, Typography, CircularProgress, Alert,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import { SpeedOutlined, NetworkCheck } from '@mui/icons-material';
import { v4 as uuidv4 } from 'uuid';

const SpeedTestPanel = ({ apiKey, clientId, clientName, onError }) => {
    const [testResults, setTestResults] = useState(null);
    const [testing, setTesting] = useState(false);
    const [error, setError] = useState('');
    const [connecting, setConnecting] = useState(false);
    const pendingTestRef = useRef(null);
    const websocketRef = useRef(null);
    const timeoutRef = useRef(null);
    const wsBaseUrl = 'wss://api1.masternode.wobot.ai';

    // Clean up function to handle connection termination and timeouts
    const cleanupConnection = () => {
        if (websocketRef.current) {
            console.log('Closing WebSocket connection');
            websocketRef.current.close();
            websocketRef.current = null;
        }
        
        if (timeoutRef.current) {
            console.log('Clearing timeout');
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        
        setConnecting(false);
    };
    
    // Handle component unmount
    useEffect(() => {
        return () => {
            cleanupConnection();
        };
    }, []);
    
    // Function to fetch WebSocket ticket
    const fetchWebSocketTicket = async () => {
        try {
            console.log('Fetching WebSocket ticket...');
            const response = await fetch(`${wsBaseUrl.replace('wss://', 'https://')}/api/auth/ws-ticket`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${apiKey}`,
                    'Content-Type': 'application/json'
                }
            });
            
            if (!response.ok) {
                throw new Error(`Failed to obtain WebSocket ticket: ${response.status} ${response.statusText}`);
            }
            
            const data = await response.json();
            if (!data.ticket) {
                throw new Error('No ticket received from server');
            }
            
            return data.ticket;
        } catch (err) {
            console.error("Error fetching WebSocket ticket:", err);
            setError(`Failed to obtain WebSocket authentication ticket: ${err.message}`);
            if (onError) onError(`WebSocket ticket fetch failed: ${err.message}`);
            throw err;
        }
    };
    
    const triggerSpeedTest = async () => {
        if (!apiKey || !clientId) {
            setError("API key or client ID not specified");
            return;
        }
        
        // Clean up any existing connection
        cleanupConnection();
        
        // Set states for starting test
        setTesting(true);
        setConnecting(true);
        setError('');
        setTestResults(null);
        
        const correlationID = uuidv4(); // Generate unique ID for this test
        pendingTestRef.current = correlationID;
        
        try {
            // Fetch a ticket for WebSocket connection
            const ticket = await fetchWebSocketTicket();
            
            // Connect to WebSocket using the ticket
            const wsUrl = `${wsBaseUrl}/ws/admin?ticket=${ticket}`;
            console.log('Connecting to WebSocket with ticket...');
            
            const ws = new WebSocket(wsUrl);
            websocketRef.current = ws;
            
            // Handle websocket events
            ws.onopen = () => {
                console.log('WebSocket connection established, sending speed test request');
                setConnecting(false);
                
                // Send the speed test request once connected
                ws.send(JSON.stringify({
                    action: "admin_trigger_speedtest",
                    payload: {
                        targetClientID: clientId,
                        correlationID: correlationID
                    }
                }));
                
                // Set a timeout for the test
                timeoutRef.current = setTimeout(() => {
                    if (pendingTestRef.current === correlationID) {
                        setError("Speed test timed out. Client may be offline or unresponsive.");
                        setTesting(false);
                        pendingTestRef.current = null;
                        cleanupConnection();
                    }
                }, 30000); // 30-second timeout
            };
            
            ws.onmessage = (event) => {
                try {
                    const message = JSON.parse(event.data);
                    console.log('Received message:', message.type);
                    
                    // Handle speed test results
                    if (message.type === 'speed_test_result_delivery') {
                        if (pendingTestRef.current === message.payload.correlationID) {
                            setTestResults(message.payload);
                            setTesting(false);
                            pendingTestRef.current = null;
                            // Close connection after receiving results
                            cleanupConnection();
                        }
                    } 
                    // Handle errors
                    else if (message.type === 'speed_test_error') {
                        if (pendingTestRef.current === message.payload.correlationID) {
                            setError(`Error: ${message.payload.error}`);
                            setTesting(false);
                            pendingTestRef.current = null;
                            // Close connection after receiving error
                            cleanupConnection();
                        }
                    }
                } catch (err) {
                    console.error("Failed to parse WebSocket message:", err);
                }
            };
            
            ws.onclose = (event) => {
                console.log(`WebSocket closed with code ${event.code}. Reason: ${event.reason || 'No reason provided'}`);
                setConnecting(false);
                
                // If we still have a pending test when the connection closes unexpectedly, report error
                if (pendingTestRef.current === correlationID && testing) {
                    setError("WebSocket connection closed unexpectedly during test");
                    setTesting(false);
                    pendingTestRef.current = null;
                }
                
                websocketRef.current = null;
            };
            
            ws.onerror = (err) => {
                console.error("WebSocket error:", err);
                setConnecting(false);
                setTesting(false);
                setError("WebSocket connection error");
                
                if (pendingTestRef.current === correlationID) {
                    pendingTestRef.current = null;
                }
                
                cleanupConnection();
            };
            
        } catch (err) {
            console.error("Failed to establish WebSocket connection:", err);
            setConnecting(false);
            setTesting(false);
            // Error already set in fetchWebSocketTicket if that's where it failed
            pendingTestRef.current = null;
        }
    };
    
    // Connection status indicator
    const connectionStatus = () => {
        if (connecting) {
            return (
                <Alert severity="info" sx={{ mt: 1 }}>
                    Connecting to server...
                </Alert>
            );
        }
        return null;
    };
    
    if (!clientId) {
        return <Alert severity="info">Select a client to run a speed test</Alert>;
    }
    return (
        <Box sx={{ background: 'rgba(255,255,255,0.02)', borderRadius: 2, p: 2, border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Button 
                    variant="contained"
                    startIcon={testing ? <CircularProgress size={20} color="inherit" /> : <SpeedOutlined />}
                    onClick={triggerSpeedTest}
                    disabled={testing || connecting}
                    sx={{ mr: 2 }}
                >
                    {testing ? "Testing..." : "Run Speed Test"}
                </Button>
                <Typography variant="body2" color="textSecondary">
                    Run a network speed test on the device to check its internet connection quality.
                </Typography>
            </Box>
            {connectionStatus()}
            {error && <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>}
            {testResults && (
                <Box sx={{ mt: 2 }}>
                    <Alert severity="success" sx={{ mb: 2 }}>
                        Speed test completed for client {clientName || clientId}
                    </Alert>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Metric</TableCell>
                                    <TableCell align="right">Result</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <TableRow>
                                    <TableCell component="th" scope="row">Latency</TableCell>
                                    <TableCell align="right">{testResults.latency}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell component="th" scope="row">Download</TableCell>
                                    <TableCell align="right">{testResults.download?.toFixed(2)} Mbps</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell component="th" scope="row">Upload</TableCell>
                                    <TableCell align="right">{testResults.upload?.toFixed(2)} Mbps</TableCell>
                                </TableRow>
                                {testResults.serverHost && (
                                    <TableRow>
                                        <TableCell component="th" scope="row">Server</TableCell>
                                        <TableCell align="right">{testResults.serverHost}</TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            )}
        </Box>
    );
};

export default SpeedTestPanel;
